import React, { useState } from 'react';
import DialogTitle from 'components/DialogOverrides/DialogTitle';
import { Dialog, Divider, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { ImportMode } from 'api/rateImportApi';
import { ImportRateScope } from './ImportRateCardMenuButton';

export type { ImportRateScope };

export interface ScopedTabProps {
  mode: ImportMode;
  onBusyChange: (busy: boolean) => void;
  onClose: () => void;
}

const TAB_LABEL: Record<ImportRateScope, Record<ImportMode, string>> = {
  default: { new: 'Import New Default Rates', update: 'Update Existing Default Rates' },
  buy: { new: 'Import New Branch Specific Buy Rates', update: 'Update Existing Branch Specific Buy Rates' },
  sell: { new: 'Import New Branch/Client Specific Sell Rates', update: 'Update Existing Branch/Client Specific Sell Rates' },
};

interface Props {
  open: boolean;
  scope: ImportRateScope;
  handleClose: () => void;
  dialogTitle: Record<ImportRateScope, string>;
  tabComponent: Record<ImportRateScope, React.FC<ScopedTabProps>>;
}

// Shared by Consumable and Equipment's bulk-import dialog
const BulkUpdateRatesDialog = ({ open, scope, handleClose, dialogTitle, tabComponent }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isBusy, setIsBusy] = useState(false);
  const [mode, setMode] = useState<ImportMode>('new');

  const onClose = () => {
    if (isBusy) return;
    handleClose();
  };

  const TabComponent = tabComponent[scope];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEnforceFocus
      fullWidth
      scroll="paper"
      fullScreen={isMobile}
      maxWidth="lg">
      <DialogTitle onClose={onClose}>{dialogTitle[scope]}</DialogTitle>
      <Divider />
      <Tabs
        value={mode}
        onChange={(_e, value) => {
          if (isBusy) return;
          setMode(value);
        }}
        variant="fullWidth">
        <Tab label={TAB_LABEL[scope].new} value="new" disabled={isBusy} />
        <Tab label={TAB_LABEL[scope].update} value="update" disabled={isBusy} />
      </Tabs>
      <Divider />

      <TabComponent
        key={mode}
        mode={mode}
        onBusyChange={setIsBusy}
        onClose={onClose}
      />
    </Dialog>
  );
};

export default BulkUpdateRatesDialog;
